import { Setting } from '../../types/Setting'
import { FeaturedGamesSetting, PerformanceSettingData, ServiceVersionInfoData, SettingKey } from '../features/settings/types/types'
import { optionalJson } from './helpers'
import { internalApiserverApi } from './internal-apiserver'

type InternalSettingMutation = FeaturedGamesSetting | { name: 'performance'; data: PerformanceSettingData; publicSetting: true }

const internalSettingsApi = internalApiserverApi.enhanceEndpoints({ addTagTypes: ['internalSetting'] }).injectEndpoints({
  endpoints: (builder) => ({
    // `null` when the setting doesn't exist.
    getInternalSetting: builder.query<Setting | null, SettingKey>({
      query: (key) => ({ url: `i/settings/apiserver/${key}`, responseHandler: optionalJson }),
      providesTags: (_result, _error, key) => [{ type: 'internalSetting', id: key }],
    }),
    // Returns the setting as stored afterwards.
    saveServiceVersionInfo: builder.mutation<Setting<ServiceVersionInfoData> | null, ServiceVersionInfoData>({
      async queryFn(data, _api, _extraOptions, baseQuery) {
        const saved = await baseQuery({
          url: 'i/settings/versioninfo',
          method: 'POST',
          body: { name: 'serviceVersionInfo', data },
          responseHandler: optionalJson,
        })
        if (saved.error) return { error: saved.error }
        const stored = await baseQuery({ url: 'i/settings/apiserver/serviceVersionInfo', responseHandler: optionalJson })
        if (stored.error) return { error: stored.error }
        return { data: stored.data as Setting<ServiceVersionInfoData> | null }
      },
      async onQueryStarted(_data, { dispatch, queryFulfilled }) {
        try {
          const { data: stored } = await queryFulfilled
          dispatch(internalSettingsApi.util.upsertQueryData('getInternalSetting', 'serviceVersionInfo', stored))
        } catch {}
      },
    }),
    // `PUT i/settings` finds the setting by name. Returns it as stored afterwards.
    saveInternalSetting: builder.mutation<Setting | null, InternalSettingMutation>({
      async queryFn(setting, _api, _extraOptions, baseQuery) {
        const saved = await baseQuery({ url: 'i/settings', method: 'PUT', body: setting, responseHandler: optionalJson })
        if (saved.error) return { error: saved.error }
        const stored = await baseQuery({ url: `i/settings/apiserver/${setting.name}`, responseHandler: optionalJson })
        if (stored.error) return { error: stored.error }
        return { data: stored.data as Setting | null }
      },
      async onQueryStarted({ name }, { dispatch, queryFulfilled }) {
        try {
          const { data: stored } = await queryFulfilled
          dispatch(internalSettingsApi.util.upsertQueryData('getInternalSetting', name as SettingKey, stored))
        } catch {}
      },
    }),
    // Both refreshes start long-running jobs over every analysis.
    refreshAnalysesTopFeatures: builder.mutation<void, void>({
      query: () => ({ url: 'i/analyses/topfeatures/refresh', method: 'POST', responseHandler: 'text' }),
    }),
    refreshAnalysesTags: builder.mutation<void, void>({
      query: () => ({ url: 'i/tags/refresh', method: 'POST', responseHandler: 'text' }),
    }),
  }),
})

export const {
  useGetInternalSettingQuery,
  useSaveServiceVersionInfoMutation,
  useSaveInternalSettingMutation,
  useRefreshAnalysesTopFeaturesMutation,
  useRefreshAnalysesTagsMutation,
} = internalSettingsApi
